import React from "react"
import { Route, Switch } from "react-router-dom"
import {
  ConnectionStatus,
  ConnectionSyncStatus,
  useListConnectionBadgeCountsQuery,
} from "@digits-graphql/frontend/graphql-bearer"
import { SvgElectrical01 } from "@digits-shared/components/SVGIcons/line/Electrical01.svg"
import useSession from "@digits-shared/hooks/useSession"
import useStateBoolean from "@digits-shared/hooks/useStateBoolean"
import { AspectCode } from "@digits-shared/session/AspectCode"
import { JWTPermissionFlag } from "@digits-shared/session/jwt/jwtPermissions"
import { ManagedSnackbar } from "src/frontend/components/Shared/ManagedModals/ManagedSnackbar"
import { ManagedSnackbarName } from "src/frontend/components/Shared/ManagedModals/state/types"
import { useHasPermission } from "src/frontend/components/Shared/Permissions/Requires"
import { useFrontendPathGenerator } from "src/frontend/hooks/useFrontendPathGenerator"
import routes from "src/frontend/routes"
import type FrontendSession from "src/frontend/session"
import {
  FrontendPermissionModule,
  FrontendPermissionSource,
} from "src/frontend/session/permissionModule"

export const ConnectAlerts: React.FC = () => {
  const { value: dismissed, setTrue: dismiss } = useStateBoolean()
  const session = useSession<FrontendSession>()
  const hasDashboard = React.useMemo(
    () => session.currentLegalEntity?.hasDashboardAccess(),
    [session.currentLegalEntity]
  )
  return (
    !dismissed &&
    hasDashboard && (
      <Switch>
        <Route
          path={routes.legalEntityHome.parameterizedPath}
          render={() => <ConnectAlert dismiss={dismiss} />}
        />
      </Switch>
    )
  )
}

const ConnectAlert: React.FC<{ dismiss: () => void }> = ({ dismiss }) => {
  const { currentLegalEntityId, hasAccessToAspect } = useSession<FrontendSession>()
  const hasAIB = hasAccessToAspect(AspectCode.AIBookkeeper)
  const hasConnectionStatusesPermission = useHasPermission({
    source: FrontendPermissionSource.LegalEntity,
    module: FrontendPermissionModule.ConnectionAccounts,
    flag: JWTPermissionFlag.Update,
  })
  const hasSyncStatusesPermission = useHasPermission({
    source: FrontendPermissionSource.LegalEntity,
    module: FrontendPermissionModule.Connections,
    flag: JWTPermissionFlag.Update,
  })
  const hasPermission = hasAIB && (hasSyncStatusesPermission || hasConnectionStatusesPermission)

  const { data: broken, loading } = useListConnectionBadgeCountsQuery({
    variables: {
      filter: {
        legalEntityIds: [currentLegalEntityId],
        connectionStatuses: hasConnectionStatusesPermission
          ? [ConnectionStatus.ActivationPending, ConnectionStatus.ConfigurationPending]
          : undefined,
        syncStatuses: hasSyncStatusesPermission ? [ConnectionSyncStatus.LoginRequired] : undefined,
      },
    },
    skip: !hasPermission || !currentLegalEntityId,
    // we only want to show this on login, so rely on cache when available
    fetchPolicy: "cache-first",
  })

  const hasErrors = React.useMemo(
    () => Boolean(broken?.listConnectionBadgeCounts?.[0]?.count),
    [broken]
  )

  const upgradeAvailable = useListConnectionBadgeCountsQuery({
    variables: {
      filter: {
        legalEntityIds: [currentLegalEntityId],
        authorizationRequired: true,
      },
    },
    skip: !hasAIB || !hasSyncStatusesPermission || !currentLegalEntityId || loading || hasErrors,
    // we only want to show this on login, so rely on cache when available
    fetchPolicy: "cache-first",
  })

  const hasUpgradeAvailable = React.useMemo(
    () => Boolean(upgradeAvailable.data?.listConnectionBadgeCounts?.[0]?.count),
    [upgradeAvailable.data]
  )

  // prefer to show the error toast first, then the upgrade toast
  if (hasErrors) {
    return <ConnectErrorAlert dismiss={dismiss} />
  }

  if (hasUpgradeAvailable) {
    return <ConnectUpgradeAvailable dismiss={dismiss} />
  }

  return null
}

const ConnectErrorAlert: React.FC<{ dismiss: () => void }> = ({ dismiss }) => {
  const { currentLegalEntity } = useSession<FrontendSession>()
  const generatePath = useFrontendPathGenerator()

  return (
    <ManagedSnackbar
      name={ManagedSnackbarName.ConnectAlert}
      hideOnActiveDetailsView
      dismissal="Session"
      message="Your connections need attention"
      description="Manage them to properly sync data to Digits"
      showIcon={false}
      onClick={dismiss}
      onCloseClick={dismiss}
      // add a slight delay so the alert doesn't open before the welcome modal
      delayMs={2_000}
      buttonContents="Manage"
      to={generatePath(routes.connections, {
        leSlug: currentLegalEntity?.slug,
        rie: 1,
      })}
    />
  )
}

const ConnectUpgradeAvailable: React.FC<{ dismiss: () => void }> = ({ dismiss }) => {
  const { currentLegalEntity } = useSession<FrontendSession>()
  const generatePath = useFrontendPathGenerator()

  const buttonContents = (
    <>
      <SvgElectrical01 />
      Authorize
    </>
  )

  return (
    <ManagedSnackbar
      name={ManagedSnackbarName.ConnectAlert}
      hideOnActiveDetailsView
      dismissal="Session"
      message="Bank Statements Available"
      description="Authorize connections to fetch bank statements"
      showIcon={false}
      showClose={true}
      onClick={dismiss}
      onCloseClick={dismiss}
      // add a slight delay so the alert doesn't open before the welcome modal
      delayMs={2_000}
      buttonVariant="primary-blue"
      buttonContents={buttonContents}
      to={generatePath(routes.connections, {
        leSlug: currentLegalEntity?.slug,
        rie: 1,
      })}
    />
  )
}
